import React, { useEffect, useState } from 'react';
import PortalModal from '../common/PortalModal/PortalModal';
import Input from '../common/Input/Input';
import Select from '../common/Select/Select';
import Button from '../common/Button/Button';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';
import { apiClient } from '../../services/apiClient';
import { toastService } from '../../services/toastService';

const REDIRECT_URI = `${window.location.origin}/oauth/mcp/callback`;

function emptyForm() {
  return {
    name: '',
    description: '',
    server_url: '',
    transport: 'http',
    auth_type: 'none',
    enabled: true,
    bearer_token: '',
    bearer_token_changed: false,
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_client_secret_changed: false,
    oauth_authorize_url: '',
    oauth_token_url: '',
    oauth_scopes: '',
    oauth_registration_endpoint: '',
  };
}

function fromServer(server) {
  const ap = server.auth_public || {};
  return {
    name: server.name,
    description: server.description || '',
    server_url: server.server_url,
    transport: server.transport,
    auth_type: server.auth_type,
    enabled: server.enabled,
    bearer_token: '',
    bearer_token_changed: false,
    oauth_client_id: ap.client_id || '',
    oauth_client_secret: '',
    oauth_client_secret_changed: false,
    oauth_authorize_url: ap.authorize_url || '',
    oauth_token_url: ap.token_url || '',
    oauth_scopes: (ap.scopes || []).join(', '),
    oauth_registration_endpoint: ap.registration_endpoint || '',
  };
}

function buildPayload(form, mode, original) {
  const isCreate = mode === 'create';
  const payload = {};

  const set = (key, value) => {
    if (isCreate) payload[key] = value;
    else if ((original[key] ?? null) !== value) payload[key] = value;
  };

  set('name', form.name);
  set('description', form.description || null);
  set('server_url', form.server_url);
  set('transport', form.transport);
  set('auth_type', form.auth_type);
  set('enabled', form.enabled);

  if (form.auth_type === 'bearer') {
    if (isCreate) payload.bearer_token = form.bearer_token;
    else if (form.bearer_token_changed) payload.bearer_token = form.bearer_token;
  }

  if (form.auth_type === 'oauth2') {
    const scopes = form.oauth_scopes.split(',').map((s) => s.trim()).filter(Boolean);
    const useDcr = !!form.oauth_registration_endpoint;
    const oauth_config = {
      authorize_url: form.oauth_authorize_url,
      token_url: form.oauth_token_url,
      redirect_uri: REDIRECT_URI,
      scopes,
    };
    if (useDcr) {
      oauth_config.registration_endpoint = form.oauth_registration_endpoint;
      // client_id is minted by the AS; don't send a stale one.
    } else {
      oauth_config.client_id = form.oauth_client_id;
      if (isCreate || form.oauth_client_secret_changed) {
        if (form.oauth_client_secret) oauth_config.client_secret = form.oauth_client_secret;
      }
    }
    if (isCreate) {
      payload.oauth_config = oauth_config;
    } else {
      const originalAp = original.auth_public || {};
      const changed = (
        (!useDcr && oauth_config.client_id !== (originalAp.client_id || '')) ||
        oauth_config.authorize_url !== (originalAp.authorize_url || '') ||
        oauth_config.token_url !== (originalAp.token_url || '') ||
        JSON.stringify(scopes) !== JSON.stringify(originalAp.scopes || []) ||
        form.oauth_registration_endpoint !== (originalAp.registration_endpoint || '') ||
        form.oauth_client_secret_changed
      );
      if (changed) payload.oauth_config = oauth_config;
    }
  }

  return payload;
}

export default function MCPServerModal({ mode, server, onClose, onSaved }) {
  const [form, setForm] = useState(() => (mode === 'edit' && server ? fromServer(server) : emptyForm()));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && server) setForm(fromServer(server));
    else setForm(emptyForm());
  }, [mode, server]);

  const onChange = (key) => (e) => {
    const value = e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onAuthTypeChange = (e) => {
    const auth_type = e.target.value;
    setForm((prev) => ({
      ...prev,
      auth_type,
      bearer_token: '',
      bearer_token_changed: false,
      oauth_client_id: auth_type === 'oauth2' ? prev.oauth_client_id : '',
      oauth_client_secret: '',
      oauth_client_secret_changed: false,
      oauth_authorize_url: auth_type === 'oauth2' ? prev.oauth_authorize_url : '',
      oauth_token_url: auth_type === 'oauth2' ? prev.oauth_token_url : '',
      oauth_scopes: auth_type === 'oauth2' ? prev.oauth_scopes : '',
      oauth_registration_endpoint: auth_type === 'oauth2' ? prev.oauth_registration_endpoint : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.server_url) {
      toastService.error('Name and Server URL are required');
      return;
    }
    if (!/^https?:\/\//.test(form.server_url)) {
      toastService.error('Server URL must start with http:// or https://');
      return;
    }
    if (form.auth_type === 'bearer') {
      const needsToken = mode === 'create' || (mode === 'edit' && server?.auth_type !== 'bearer');
      if (needsToken && !form.bearer_token) {
        toastService.error('Bearer token is required');
        return;
      }
    }
    if (form.auth_type === 'oauth2') {
      const useDcr = !!form.oauth_registration_endpoint;
      if (!form.oauth_authorize_url || !form.oauth_token_url) {
        toastService.error('Authorize URL and Token URL are required');
        return;
      }
      if (!useDcr && !form.oauth_client_id) {
        toastService.error('Client ID is required (or provide a Registration endpoint for DCR)');
        return;
      }
      // Client secret is optional: public clients (PKCE-only) and DCR-issued
      // clients without a secret are both supported.
    }
    setSaving(true);
    try {
      const payload = buildPayload(form, mode, server || {});
      if (mode === 'create') {
        const created = await apiClient.createMcpServer(payload);
        toastService.success(`Created ${created.name}`);
        onSaved(created);
      } else {
        const updated = await apiClient.patchMcpServer(server.id, payload);
        toastService.success(`Updated ${updated.name}`);
        onSaved(updated);
      }
      onClose();
    } catch (e) {
      // toast already fired by apiClient.handleError
    } finally {
      setSaving(false);
    }
  };

  const copyRedirectUri = () => {
    navigator.clipboard.writeText(REDIRECT_URI);
    toastService.success('Redirect URI copied');
  };

  return (
    <PortalModal isOpen onClose={onClose} title={mode === 'create' ? 'Add MCP Server' : `Edit ${server?.name}`}>
      <form onSubmit={handleSubmit} className="mcp-modal-form">
        <Input label="Name" value={form.name} onChange={onChange('name')} required />
        <label className="mcp-modal-label">
          Description
          <textarea
            value={form.description}
            onChange={(e) => onChange('description')(e.target.value)}
            rows={2}
          />
        </label>
        <Input label="Server URL" value={form.server_url} onChange={onChange('server_url')} required />
        <Select
          label="Transport"
          value={form.transport}
          onChange={(e) => onChange('transport')(e.target.value)}
          options={[
            { value: 'http', label: 'HTTP' },
            { value: 'sse', label: 'SSE' },
          ]}
        />
        <Select
          label="Auth type"
          value={form.auth_type}
          onChange={onAuthTypeChange}
          options={[
            { value: 'none', label: 'None' },
            { value: 'bearer', label: 'Bearer token' },
            { value: 'oauth2', label: 'OAuth 2.0' },
          ]}
        />
        <div className="mcp-toggle-row">
          <ToggleSwitch checked={form.enabled} onChange={(v) => onChange('enabled')(v)} />
          <span>Enabled</span>
        </div>

        {form.auth_type === 'bearer' && (
          <Input
            label="Bearer token"
            type="password"
            placeholder={mode === 'edit' ? '•••• (configured)' : ''}
            value={form.bearer_token}
            onChange={(e) => {
              onChange('bearer_token')(e.target.value);
              setForm((p) => ({ ...p, bearer_token_changed: true }));
            }}
          />
        )}

        {form.auth_type === 'oauth2' && (
          <>
            <Input
              label="Registration endpoint (optional — for Dynamic Client Registration / RFC 7591)"
              value={form.oauth_registration_endpoint}
              onChange={onChange('oauth_registration_endpoint')}
              placeholder="e.g. https://cf.mcp.atlassian.com/v1/register"
            />
            <Input
              label={form.oauth_registration_endpoint
                ? 'Client ID (will be auto-issued via DCR on save)'
                : 'Client ID'}
              value={form.oauth_client_id}
              onChange={onChange('oauth_client_id')}
              disabled={!!form.oauth_registration_endpoint && mode === 'create'}
              required={!form.oauth_registration_endpoint}
            />
            <Input
              label="Client secret (optional — leave blank for public / PKCE-only clients)"
              type="password"
              placeholder={mode === 'edit' ? '•••• (configured)' : 'optional'}
              value={form.oauth_client_secret}
              onChange={(e) => {
                onChange('oauth_client_secret')(e.target.value);
                setForm((p) => ({ ...p, oauth_client_secret_changed: true }));
              }}
              disabled={!!form.oauth_registration_endpoint && mode === 'create'}
            />
            <Input label="Authorize URL" value={form.oauth_authorize_url} onChange={onChange('oauth_authorize_url')} required />
            <Input label="Token URL" value={form.oauth_token_url} onChange={onChange('oauth_token_url')} required />
            <Input label="Scopes (comma-separated)" value={form.oauth_scopes} onChange={onChange('oauth_scopes')} />
            <div className="mcp-redirect-row">
              <label>Redirect URI</label>
              <code>{REDIRECT_URI}</code>
              <Button type="button" size="sm" onClick={copyRedirectUri}>Copy</Button>
            </div>
          </>
        )}

        <div className="mcp-modal-actions">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving} isLoading={saving}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </div>
      </form>
    </PortalModal>
  );
}
